/* stylelint-disable property-no-vendor-prefix */
import React, { FormEvent } from 'react'
import MediaQuery from 'react-responsive'

import { Ink } from '@acorns/web-components'
import { find } from 'ramda'
import {
  compose,
  defaultProps,
  pure,
  withHandlers,
  withProps,
  withState,
} from 'recompose'
import styled from 'styled-components'

import DisabledLockPath from 'assets/icons/lock-disabled.svg'

import breakpoints, { theme } from '../../../theme'
import withClickOutside from '../../HOCs/withClickOutside'
import Check from '../check.svg'
import Chevron from '../chevron.svg'

export type Option = {
  label: string
  value: string
  extra?: any
}

export type Props = {
  className?: string
  disabled?: boolean
  error?: boolean
  errorText?: string | JSX.Element
  id?: string
  label?: string | JSX.Element
  name?: string
  onChange: (field: string, value: any, shouldValidate?: boolean) => void
  onFocus?: () => void
  options: Option[]
  placeholder?: string
  position?: string
  renderMenu?: (props: InnerProps) => JSX.Element
  renderSelected?: (selectedOption: Option, props: InnerProps) => JSX.Element
  subtext?: string
  useNative?: boolean
  value: string
  toggleInputs?: (isActive: boolean) => void
  testId?: string
  hideOverlay?: boolean
}

type DefaultProps = {
  renderOption: (
    label: string,
    selected: boolean,
    value?: string,
  ) => JSX.Element
}

type AddedProps = {
  selectedOption: Option
  selectedLabel: string
}

type StateProps = {
  active: boolean
  container: HTMLDivElement
  listElement: HTMLLIElement
  offsetTop: number
  setContainer: (container: HTMLDivElement) => void
  setListElement: (listElement: HTMLLIElement) => void
  setOffsetTop: (offsetTop: number) => void
  setActive: (active: boolean) => void
}

type HandlerProps = {
  handleChange: (event: React.ChangeEvent<HTMLSelectElement>) => void
  handleContainerRef: (ref: HTMLDivElement) => void
  handleKeyUp: (event: React.KeyboardEvent<HTMLSelectElement>) => void
  handleMouseDown: (event: FormEvent<HTMLSelectElement>) => void
  handleOptionClick: (event: React.MouseEvent<HTMLElement>) => void
  handleOverlayClick: () => void
  toggleActive: (active: boolean) => void
  closeInput: () => void
}

export type InnerProps = Props &
  AddedProps &
  StateProps &
  DefaultProps &
  HandlerProps

const Container = styled.div`
  display: block;
  position: relative;
  user-select: none;
  transition: opacity 150ms ease-in-out;

  /* Note: This gives us room to render an error label below the text field */
  padding-bottom: 30px;
`

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  color: ${theme('colors.slate')};
  margin-bottom: 8px;
`

const ErrorLabel = styled(Label)`
  color: ${theme('colors.red')};
  font-size: 12px;
  font-weight: 300;
  left: 0;
  margin-bottom: 0;
  margin-top: 3px;
  position: absolute;
`

const Select = styled('select')<{
  'data-error'?: boolean
  placeholderVisible?: any
}>`
  background: url('${Chevron}') no-repeat center right 16px;
  border-radius: ${theme('styles.borderRadius')};
  border: 1px solid ${theme('colors.stoneLight')};
  color: ${theme('colors.slate')};
  cursor: pointer;
  font-size: 14px;
  height: 44px;
  padding: 0 16px;
  width: 100%;

  &:disabled {
    cursor: default;
  }

  &:focus {
    outline: 0;
  }

  @media screen and (max-width: ${theme('breakpoints.sm')}px) {
    font-size: 14px;
  }

  ${(props) =>
    props['data-error'] &&
    `
    border-color: ${props.theme.colors.red};
    color: ${props.theme.colors.red};
  `}

  ${(props) =>
    props.placeholderVisible &&
    `
    color: ${props.theme.colors.stoneLight};
  `}
  ${(props) =>
    props.disabled &&
    `
      cursor: default
      background: url('${DisabledLockPath}') no-repeat center right 16px;
      background-size: 10px 14px;
      color: ${props.theme.colors.stone};
      background-color: #fbfbfb;

    `};

  -webkit-appearance: none;
  -webkit-border-radius: ${theme('styles.borderRadius')};

  -moz-appearance: none;
  text-indent: 0.01px;
  text-overflow: '';
`

const MobilePlaceHolder = Select.withComponent('input')

export const Options = styled('div')<{
  position?: string
  label?: string | JSX.Element
  offsetTop: number
}>`
  padding: 0;
  list-style: none;
  background-color: #fff;
  border-radius: ${theme('styles.borderRadius')};
  box-shadow: ${theme('shadows.lgMedium')};
  left: 0;
  margin: 10px 0 0;
  max-height: 356px;
  overflow: auto;
  position: absolute;
  right: 0;
  top: ${(props) => (props.label ? '12px' : '-12px')};
  z-index: 101;

  ${(props) =>
    props.position === 'top' &&
    `
  bottom: 0;
  margin-bottom: 10px;
  top: auto;
`} li:not(:last-child) {
    border-bottom: 1px solid ${theme('colors.ivory')};
  }

  ${(props) =>
    props.offsetTop &&
    `
  top: ${props.offsetTop}px;
`};

  @media screen and (max-width: ${theme('breakpoints.md')}px) {
    bottom: 20px;
    left: 20px;
    position: fixed;
    right: 20px;
    top: auto;
    width: auto;
    max-height: 60vh;
    overflow: scroll;
    &::before {
      display: none;
    }
  }
`

const Option = styled.li`
  position: relative;
  align-items: center;
  color: ${theme('colors.slate')};
  cursor: pointer;
  display: flex;
  font-size: 14px;
  height: 60px;
  justify-content: space-between;
  padding: 0 20px;
  transition: opacity 200ms;
`
const SubText = styled(Option)`
  cursor: auto;
  color: ${theme('colors.stone')};
  text-align: center;
  justify-content: center;
  font-size: 12px;
  font-style: italic;
  padding: 20px 108px;
  line-height: 1.333;

  @media (max-width: ${theme('breakpoints.md')}px) {
    padding: 20px 20px;
  }
`

const CheckMark = styled.div`
  background-color: ${theme('colors.green')};
  border-radius: ${theme('styles.borderRadius')};
  display: inline-block;
  height: 20px;
  width: 20px;

  &:after {
    background-size: 12px 9px;
    background: url('${Check}') no-repeat center center;
    content: '';
    display: block;
    height: 100%;
    width: 100%;
  }
`

const Value = styled<any>('div')`
  bottom: 30px;
  color: ${theme('colors.slate')};
  font-size: 14px;
  padding: 14px 16px;
  position: absolute;
  pointer-events: none;

  ${(props: any) =>
    props['data-error'] &&
    `
    color: ${props.theme.colors.red};
  `};

  ${(props: any) =>
    props.disabled &&
    `
    color: ${props.theme.colors.stone};
    `};
`

const Overlay = styled.div`
  background-color: ${theme('colors.slate')};
  bottom: 0;
  left: 0;
  opacity: 0.6;
  position: fixed;
  right: 0;
  top: 70px;
  width: 100vh;
  height: 100vh;
  z-index: 100;
`

const Placeholder = styled(Value)`
  color: ${theme('colors.stoneLight')};
`

const enhance = compose<InnerProps, Props>(
  defaultProps({
    disabled: false,
    useNative: /iPhone|iPod|Android/i.test(navigator.userAgent),
    position: 'bottom',
    renderOption: (label: string, selected: boolean) => (
      <>
        {label}
        {selected && <CheckMark />}
      </>
    ),
    renderSelected: (_: any, props: any) => (
      <Value data-error={props.error} disabled={props.disabled}>
        {props.selectedLabel}
      </Value>
    ),
  }),

  withState('active', 'setActive', false),
  withState('container', 'setContainer', null),
  withState('listElement', 'setListElement', null),
  withState('offsetTop', 'setOffsetTop', null),

  withProps<Partial<InnerProps>, Props>(({ options, value }) => {
    const selectedOption = options.find((option) => option.value === value)
    return {
      selectedOption,
      selectedLabel: selectedOption ? selectedOption.label : '',
    }
  }),

  withHandlers<InnerProps, Partial<HandlerProps>>({
    toggleActive:
      ({ setActive, toggleInputs, useNative, onFocus }) =>
      (isActive) => {
        setActive(isActive)
        if (!useNative && isActive && onFocus) {
          onFocus()
        }
        if (!useNative && toggleInputs) {
          toggleInputs(isActive)
        }
      },
  }),
  withHandlers<InnerProps, Partial<HandlerProps>>({
    handleChange:
      ({ onChange }) =>
      ({ target }) => {
        const field = target.name || target.id || (target as any).label
        onChange(field, target.value)
      },
    handleContainerRef:
      ({ container, setContainer }) =>
      (element) => {
        if (!container) {
          setContainer(element)
        }
      },
    handleKeyUp:
      ({ listElement, useNative }) =>
      ({ key }) => {
        const normalizedKey = key.toLowerCase()

        if (/[a-z]/.test(normalizedKey) && !useNative) {
          const itemNodes: ReadonlyArray<HTMLLIElement> = Array.from(
            listElement.querySelectorAll('li'),
          )

          const activeItem = find<HTMLLIElement>(
            (item) => item?.dataset?.value?.[0].toLowerCase() === normalizedKey,
            itemNodes,
          )

          if (activeItem) {
            listElement.scrollTo(0, activeItem.offsetTop)
          }
        }
      },
    handleMouseDown:
      ({ toggleActive, useNative, disabled }) =>
      () => {
        if (!useNative) {
          if (!disabled) {
            toggleActive(true)
          }
        }
      },

    handleOptionClick:
      ({ id, label, name, onChange, toggleActive }) =>
      ({ currentTarget }) => {
        const field = (name || id || label) as string
        onChange(field, (currentTarget as HTMLLIElement).dataset.value)
        toggleActive(false)
      },

    closeInput:
      ({ toggleActive }) =>
      () =>
        toggleActive(false),
    handleOverlayClick:
      ({ toggleActive }) =>
      () =>
        toggleActive(false),
  }),
  withClickOutside(({ toggleActive }: InnerProps) => {
    toggleActive(false)
  }, 'mousedown'),
  pure,
)

export default enhance((props: InnerProps) => (
  <Container
    className={props.className}
    ref={props.handleContainerRef}
    data-disabled={props.disabled}
    data-testid={props.id ? `${props.id}_container` : null}
  >
    {props.label && <Label htmlFor={props.id}>{props.label}</Label>}
    {props.useNative ? (
      <Select
        placeholderVisible={!props.value && props.placeholder}
        data-error={props.error}
        id={props.id}
        data-testid={props.testId || props.id}
        name={props.name}
        onChange={props.handleChange}
        onMouseDown={props.handleMouseDown}
        onKeyUp={props.handleKeyUp}
        value={props.value}
        disabled={props.disabled}
      >
        <>
          {props.placeholder && (
            <option value="" disabled hidden>
              {props.placeholder}
            </option>
          )}
          {props.options.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </>
      </Select>
    ) : (
      <MobilePlaceHolder
        disabled={props.disabled}
        onMouseDown={
          props.handleMouseDown as any as (
            event: React.MouseEvent<HTMLInputElement, MouseEvent>,
          ) => void
        }
        data-testid={props.testId || props.id}
        data-error={props.error}
        id={props.id}
      />
    )}
    {props.error && props.errorText && (
      <ErrorLabel>{props.errorText}</ErrorLabel>
    )}

    {!props.useNative && props.active && (
      <>
        <MediaQuery query={`(max-width: ${breakpoints.breakpoints.md}px)`}>
          {!props.hideOverlay && <Overlay onClick={props.handleOverlayClick} />}
        </MediaQuery>
        <Options
          ref={props.setListElement as any}
          label={props.label}
          offsetTop={props.offsetTop}
          position={props.position}
          role="list"
          data-testid="option_dropDown"
          onFocus={props.onFocus}
        >
          {props.renderMenu
            ? props.renderMenu(props)
            : props.options.map(({ label, value }) => (
                <Option
                  data-value={value}
                  key={value}
                  onClick={props.handleOptionClick}
                  role="listitem"
                  tabIndex={0}
                  data-testid="options"
                >
                  {props.renderOption(label, value === props.value, value)}
                  <Ink opacity={0.02} />
                </Option>
              ))}
          {props.subtext && <SubText>{props.subtext}</SubText>}
        </Options>
      </>
    )}

    {!props.useNative && props.renderSelected?.(props.selectedOption, props)}

    {!props.useNative && !props.value && props.placeholder && (
      <Placeholder>{props.placeholder}</Placeholder>
    )}
  </Container>
)) as React.ComponentClass<Props>
